// FC-011 item 4: WordAG → FLASH_CARDS generation mapping.
//
// Explicit mapping (authorized 2026-10-27):
//   - Mode: Pick One.
//   - Both faces are TEXT.
//   - One face shows the WORD (spelled correctly), the other shows the MISSPELLING.
//   - The WORD side is the correct side (learner clicks Check Answer on the word).
//   - Options: fontSize 48, fontColor black.
//
// This hooks the EXISTING WordAG pipeline (actions/flinkMakeActions/wordlists.js):
//   checkWordAgs / generateWordActivityGroup look up
//   ActivityTemplates[alias].generateActivityFromWords({ words, template, wordAgs })
//   and ActivityTemplates[alias].validateWords(filteredWords, template), and save the
//   returned `gameData` onto the activity (data.gameData). We mirror the exact
//   contract used by find-the-one / missing-elements — no new pipeline, no backend
//   generator, no new UI knob, no admin-record write.
//
// gameData shape matches the FLASH_CARDS runtime (FlashCards.jsx / DATA-CONTRACT):
//   { mode:'pick-one', sideDefinitions:[{title,type:'text'},{title,type:'text'}],
//     problems:[{ id, items:[{text},{text}], correctSideIndex }] }
// Options font 48 / black are returned SEPARATELY as `result.options` and applied by
// the pipeline's generator options-return seam (generateWordActivityGroup merges them
// over data.options). They are NOT inside gameData and the player applies NO override —
// the saved activity's data.options carries them, read by both OptionsUI and player.
// This keeps font 48/black a real persistence fact for FLASH_CARDS generation only,
// with no change to any template's authoring defaults and no DB write.
//
// Stored text is the ACTUAL wordlist value (the pipeline already applies its own
// removeParenthesis before calling us). We do NOT trim/casefold the stored text —
// trimming is used ONLY for the emptiness/equality VALIDATION check below.
// Pure + component-free so it is unit-testable without the react-dnd barrel.

import shortId from 'shortid';

// MODE.PICK_ONE from ./flashCardsHelpers is the string 'pick-one'. We inline the
// literal here instead of importing flashCardsHelpers, because that module imports
// `utils` (the barrel) which pulls react-dnd — making this pure helper untestable in
// isolation. Keeping the literal keeps this file barrel-free and unit-testable.
const MODE_PICK_ONE = 'pick-one';

const norm = (s) => (typeof s === 'string' ? s : '').trim();

// Each generated card needs a WORD and a distinct MISSPELLING. A missing or blank
// misspelling is EXPLICITLY rejected (never a bogus identical-faces card). A
// misspelling equal to the word (case-insensitive, ignoring surrounding whitespace)
// is also rejected: it would make the two faces indistinguishable, which is not a
// valid Pick One card. This comparison does not alter the stored text.
export const wordHasMisspelling = (w) => {
  if (!w) return false;
  const word = norm(w.word);
  const miss = norm(w.misspelling);
  if (!word || !miss) return false;
  if (word.toLocaleLowerCase() === miss.toLocaleLowerCase()) return false;
  return true;
};

// validateWords(filteredWords, templateDBrecord) -> { success, error?, filteredWords }
// Matches the shape the pipeline expects. Requires every word carry a usable
// misspelling; names the offending words so the editor can fix them. No new policy
// beyond the explicit QA word/misspelling requirement.
export const validateWords = (words /* , templateDBrecord */) => {
  const list = Array.isArray(words) ? words : [];
  if (!list.length) {
    return { success: false, error: 'Should be at least 1 word' };
  }
  const missing = list.filter((w) => !wordHasMisspelling(w)).map((w) => norm(w && w.word) || '(unknown word)');
  if (missing.length) {
    return {
      success: false,
      filteredWords: list,
      error:
        'Flash Cards (Pick One) requires a distinct misspelling for each word; '
        + `missing/invalid for: ${missing.join(', ')}`,
    };
  }
  return { success: true, filteredWords: list };
};

// generateActivityFromWords({ words, template, wordAgs }) -> { gameData }
// One Pick One card per word: side 0 = word (correct), side 1 = misspelling.
// Text-only, so no wordsImages / wordsAudio / definitionsAudio are produced.
// The stored face text is the ACTUAL wordlist value (not trimmed/casefolded).
export const generateActivityFromWords = ({ words /* , template, wordAgs */ } = {}) => {
  const list = Array.isArray(words) ? words : [];

  // Fail closed on any invalid word rather than emitting a bogus identical-faces card.
  const invalid = list.filter((w) => !wordHasMisspelling(w)).map((w) => norm(w && w.word) || '(unknown word)');
  if (invalid.length) {
    return { error: `Missing/invalid misspelling for: ${invalid.join(', ')}` };
  }

  const WORD_SIDE = 0;
  const MISSPELL_SIDE = 1;

  const problems = list.map((w) => {
    const items = [];
    // Preserve the actual wordlist strings verbatim (the pipeline already did its
    // own removeParenthesis upstream); do not trim/normalize the stored text here.
    items[WORD_SIDE] = { text: typeof w.word === 'string' ? w.word : '' };
    items[MISSPELL_SIDE] = { text: typeof w.misspelling === 'string' ? w.misspelling : '' };
    return {
      id: shortId.generate(),
      items,
      // The WORD side is correct. correctSideIndex follows the actual generated
      // word face (WORD_SIDE), not a guess.
      correctSideIndex: WORD_SIDE,
      // optional per-card instruction stays empty for generated cards
      instruction: { text: '', audio: '' },
    };
  });

  const gameData = {
    mode: MODE_PICK_ONE,
    sideDefinitions: [
      { title: 'Side 1', type: 'text' },
      { title: 'Side 2', type: 'text' },
    ],
    problems,
  };

  // FC-011 item 4: required generated-activity options — Pick One font 48, black.
  // Returned via the pipeline's generator OPTIONS-RETURN seam (generateWordActivityGroup
  // merges these OVER data.options, which the OptionsUI and the player both read). This
  // is a real PERSISTENCE contract, NOT a player-only override: the saved activity's
  // data.options carries fontSize 48 / fontColor '#000'. Only FLASH_CARDS returns
  // options, so no other template's generated options change, and no authoring default
  // is altered. A missing/absent key keeps the template default.
  const options = { fontSize: 48, fontColor: '#000' };

  return { gameData, options };
};
